import React from "react";
import * as MP4Box from "mp4box";
import { extractAvcDescription, sampleTimingToMicros } from "../../utils/mp4Demux";
import { registerSeek, unregisterSeek } from "../../utils/recorderSeekRegistry";

/**
 * Recorder-mode video renderer.
 *
 * In recorder mode the page never plays the <video> element — the host
 * drives time via __SCENE_RECORDER_SEEK_FRAME(N) and screenshots the
 * page per frame. The default <video> element decoder pipeline is
 * unreliable under that workload (seek/decode/composite races leave
 * stale frames in the captured screenshot).
 *
 * This component replaces <video> with <canvas> + WebCodecs:
 *   1. Fetch the MP4 file
 *   2. mp4box.js demuxes the container into encoded video chunks
 *   3. Configure WebCodecs.VideoDecoder with the AVC description bytes
 *   4. Register a seek handler in recorderSeekRegistry. On seek(t):
 *      feed chunks until the decoder outputs a frame at >= t, paint it
 *      to canvas, resolve the seek
 *   5. Audio is supplied entirely by the offline FFmpeg manifest mix —
 *      this canvas pipeline only renders video frames
 *
 * Sequential 0→N seek pattern is the common case (recorder loop). The
 * decoder advances through chunks naturally; cache footprint is the
 * full encoded bitstream (held for backward seeks if any) plus the
 * single most-recently-painted VideoFrame (released on next paint).
 */
export default class RecorderCanvasVideo extends React.Component {
  constructor(props) {
    super(props);
    this.canvasRef = React.createRef();
    this._mounted = false;
    this._decoder = null;
    this._chunks = [];
    this._chunkIndex = 0;
    this._timescale = 0;
    this._lastPaintedFrame = null;
    // Frames the decoder emitted while no seek was pending (it was fed ahead
    // to flush its reorder buffer). Consumed by the next _seek, never painted
    // early — painting them would run the canvas ahead of the timeline.
    this._pendingFrames = [];
    this._currentSeekResolve = null;
    this._currentSeekTargetUs = -1;
    this._readyFiredForBoxId = false;
    // Loop support (props.loop): full decoded-media duration + the decoder
    // config needed to re-configure() after reset() on wrap.
    this._mediaDurationUs = 0;
    this._decoderConfig = null;
    this._lastTargetUs = -1;
    this._setupDone = new Promise((resolve) => {
      this._resolveSetupDone = resolve;
    });
  }

  async componentDidMount() {
    this._mounted = true;
    // Register the seek handler BEFORE setup so the host's frame loop
    // (runAllSeeks awaits every handler) stalls on a piece that mounts
    // mid-scene instead of capturing its unpainted box while the decoder
    // catches up to the in-point. Capped so a broken setup can't hang
    // the recorder forever.
    const seekHandler = async (t) => {
      await Promise.race([this._setupDone, new Promise((r) => setTimeout(r, 15000))]);
      return this._seek(t);
    };
    if (typeof this.props.registerSeek === "function") {
      this.props.registerSeek(this.props.boxId, seekHandler);
    } else {
      registerSeek(this.props.boxId, seekHandler);
    }
    try {
      await this._setup();
    } catch (err) {
      // Failure here means the page-side video can't render; the host
      // will screenshot a blank canvas. Surface to console for diagnosis.
      // eslint-disable-next-line no-console
      console.warn(
        "[RecorderCanvasVideo] setup failed for boxId=" + this.props.boxId + ":",
        err && err.message ? err.message : err
      );
      // Still fire onReady so the visor's ready-gate doesn't deadlock.
      this._fireReady();
    } finally {
      this._resolveSetupDone();
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    if (typeof this.props.unregisterSeek === "function") {
      this.props.unregisterSeek(this.props.boxId);
    } else {
      unregisterSeek(this.props.boxId);
    }
    if (this._lastPaintedFrame) {
      try {
        this._lastPaintedFrame.close();
      } catch (_) {}
      this._lastPaintedFrame = null;
    }
    this._dropPendingFrames();
    if (this._decoder) {
      try {
        this._decoder.close();
      } catch (_) {}
      this._decoder = null;
    }
  }

  async _setup() {
    // eslint-disable-next-line no-console
    console.log(
      "[RecorderCanvasVideo] setup start boxId=" + this.props.boxId + " url=" + this.props.url
    );
    // 1. Fetch the encoded MP4 fully (small enough — ~14 MB per typical
    //    22s clip — and simpler than streaming append). VisorPreloadManager
    //    already blob-downloaded this URL in full before
    const cachedBlobUrl =
      typeof window !== "undefined" &&
      window.__visorVideoBlobUrls &&
      window.__visorVideoBlobUrls[this.props.url];
    const response = await fetch(cachedBlobUrl || this.props.url);
    if (!response.ok) {
      throw new Error("HTTP " + response.status + " fetching " + this.props.url);
    }
    const buf = await response.arrayBuffer();

    // 2. Configure mp4box demux + decoder once info is parsed.
    const file = MP4Box.createFile();
    const setupComplete = new Promise((resolve, reject) => {
      file.onError = (e) => reject(new Error("mp4box error: " + e));
      file.onReady = (info) => {
        try {
          const track = info.videoTracks && info.videoTracks[0];
          if (!track) {
            reject(new Error("no video track in mp4"));
            return;
          }
          this._timescale = track.timescale;

          // Extract avcC. mp4box v2.x exposes the box hierarchy via
          // file.moov.traks[].mdia... Track from info.videoTracks is a
          // summary view; for the full hierarchy we use the file object
          // directly via getTrackById, OR walk file.moov.traks.
          let avcC = null;
          try {
            // Path 1: walk file.moov.traks[i] looking for matching track id
            if (file.moov && Array.isArray(file.moov.traks)) {
              for (const trak of file.moov.traks) {
                if (trak.tkhd && trak.tkhd.track_id === track.id) {
                  const entries =
                    trak.mdia &&
                    trak.mdia.minf &&
                    trak.mdia.minf.stbl &&
                    trak.mdia.minf.stbl.stsd &&
                    trak.mdia.minf.stbl.stsd.entries;
                  if (entries && entries[0]) {
                    avcC = entries[0].avcC;
                    break;
                  }
                }
              }
            }
            // Path 2: track itself may carry it
            if (!avcC && track.avcC) avcC = track.avcC;
            // Path 3: track.mdia path (older API)
            if (
              !avcC &&
              track.mdia &&
              track.mdia.minf &&
              track.mdia.minf.stbl &&
              track.mdia.minf.stbl.stsd
            ) {
              const e = track.mdia.minf.stbl.stsd.entries;
              if (e && e[0]) avcC = e[0].avcC;
            }
          } catch (_) {}
          if (avcC) {
            // eslint-disable-next-line no-console
            console.log(
              "[RecorderCanvasVideo] avcC found. keys=" +
                Object.keys(avcC).join(",") +
                " SPS type=" +
                (avcC.SPS
                  ? typeof avcC.SPS +
                    "/" +
                    (Array.isArray(avcC.SPS) ? "array" : "obj") +
                    " len=" +
                    (avcC.SPS.length || "n/a") +
                    " sps0_keys=" +
                    (avcC.SPS[0] ? Object.keys(avcC.SPS[0]).join("|") : "no sps0")
                  : "no SPS") +
                " PPS type=" +
                (avcC.PPS
                  ? typeof avcC.PPS + "/" + (Array.isArray(avcC.PPS) ? "array" : "obj")
                  : "no PPS")
            );
          }
          if (!avcC) {
            // Diagnostic: dump what we DO see on the track + file.
            // eslint-disable-next-line no-console
            console.log(
              "[RecorderCanvasVideo] avcC NOT FOUND. track keys=" +
                Object.keys(track).join(",") +
                " file.moov keys=" +
                (file.moov ? Object.keys(file.moov).join(",") : "no moov") +
                " traks count=" +
                (file.moov && file.moov.traks ? file.moov.traks.length : "n/a")
            );
            if (file.moov && file.moov.traks && file.moov.traks[0]) {
              const t0 = file.moov.traks[0];
              // eslint-disable-next-line no-console
              console.log(
                "[RecorderCanvasVideo] traks[0] keys=" +
                  Object.keys(t0).join(",") +
                  " mdia keys=" +
                  (t0.mdia ? Object.keys(t0.mdia).join(",") : "no mdia")
              );
              if (t0.mdia && t0.mdia.minf && t0.mdia.minf.stbl && t0.mdia.minf.stbl.stsd) {
                const stsd = t0.mdia.minf.stbl.stsd;
                // eslint-disable-next-line no-console
                console.log(
                  "[RecorderCanvasVideo] stsd keys=" +
                    Object.keys(stsd).join(",") +
                    " entries[0] keys=" +
                    (stsd.entries && stsd.entries[0]
                      ? Object.keys(stsd.entries[0]).join(",")
                      : "no entries")
                );
              }
            }
            reject(new Error("avcC not present (non-H.264 stream?)"));
            return;
          }
          const description = extractAvcDescription(avcC);
          // eslint-disable-next-line no-console
          console.log(
            "[RecorderCanvasVideo] description bytes=" +
              description.byteLength +
              " codec=" +
              track.codec +
              " w=" +
              track.video.width +
              " h=" +
              track.video.height
          );

          if (typeof VideoDecoder === "undefined") {
            // eslint-disable-next-line no-console
            console.log("[RecorderCanvasVideo] VideoDecoder is UNDEFINED — WebCodecs unavailable");
            reject(new Error("VideoDecoder unavailable"));
            return;
          }

          this._decoder = new VideoDecoder({
            output: (frame) => this._onDecodedFrame(frame),
            error: (err) =>
              // eslint-disable-next-line no-console
              console.warn("[RecorderCanvasVideo] decoder error:", err.message),
          });
          this._decoderConfig = {
            codec: track.codec,
            codedWidth: track.video.width,
            codedHeight: track.video.height,
            description,
          };
          this._decoder.configure(this._decoderConfig);
          // eslint-disable-next-line no-console
          console.log("[RecorderCanvasVideo] decoder configured ok");

          // Capture the canvas at the video's native resolution so paint
          // is 1:1 (Puppeteer scales for screenshot via clip).
          const c = this.canvasRef.current;
          if (c) {
            c.width = track.video.width;
            c.height = track.video.height;
          }

          file.setExtractionOptions(track.id, null, { nbSamples: 100 });
          // mp4box requires start() INSIDE onReady (before subsequent
          // appendBuffer/flush) so onSamples fires. Calling it later
          // produces an empty chunks array.
          file.start();
          // eslint-disable-next-line no-console
          console.log("[RecorderCanvasVideo] start() called, resolving");
          resolve();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log(
            "[RecorderCanvasVideo] onReady catch: " + (err && err.message ? err.message : err)
          );
          reject(err);
        }
      };
      file.onSamples = (id, _user, samples) => {
        const ts = this._timescale || 1;
        for (const sample of samples) {
          const timing = sampleTimingToMicros(sample, ts);
          this._chunks.push(
            new EncodedVideoChunk({
              type: sample.is_sync ? "key" : "delta",
              timestamp: timing.timestamp,
              duration: timing.duration,
              data: sample.data,
            })
          );
        }
      };
    });

    // mp4box requires .fileStart on the buffer.
    buf.fileStart = 0;
    // Order: appendBuffer → start (inside onReady) → flush. flush
    // triggers extraction, which fires onSamples now that start has
    // been called.
    file.appendBuffer(buf);
    await setupComplete;
    file.flush();

    // Wait until at least one batch of samples has been extracted by
    // mp4box's onSamples callback. mp4box dispatches synchronously
    // after flush given a fully-buffered file, but we poll-wait up to
    // 2 s as a safety net so we never accept seek calls with an empty
    // chunks array.
    const SAMPLES_WAIT_DEADLINE_MS = 2000;
    const startedAt = Date.now();
    while (this._chunks.length === 0 && Date.now() - startedAt < SAMPLES_WAIT_DEADLINE_MS) {
      await new Promise((r) => setTimeout(r, 25));
    }
    // eslint-disable-next-line no-console
    console.log(
      "[RecorderCanvasVideo] post-extract chunks=" +
        this._chunks.length +
        " boxId=" +
        this.props.boxId
    );
    if (this._chunks.length > 0) {
      const last = this._chunks[this._chunks.length - 1];
      this._mediaDurationUs = last.timestamp + (last.duration || 0);
      // eslint-disable-next-line no-console
      console.log(
        "[RecorderCanvasVideo] mediaDurationUs=" +
          this._mediaDurationUs +
          " boxId=" +
          this.props.boxId +
          " loop=" +
          !!this.props.loop
      );
    }

    // 3. Pre-roll trimmed/split pieces to the in-point before the ready-gate
    // lifts / the mount-registered seek handler unblocks. Decoding starts at
    // the nearest keyframe at or before the in-point instead of chunk 0 — a
    // deep in-point (minutes into the source) would otherwise mean decoding
    // thousands of frames before the first paint.
    const preRollTargetUs = Math.round((this.props.trimStartSec || 0) * 1e6);
    if (preRollTargetUs > 0 && this._chunks.length > 0) {
      this._chunkIndex = this._keyIndexAtOrBefore(preRollTargetUs);
      const deadline = Date.now() + 8000;
      while (this._mounted && Date.now() < deadline) {
        await this._seek(this.props.clipStartSec || 0);
        if (this._lastPaintedFrame && this._lastPaintedFrame.timestamp >= preRollTargetUs - 50000) {
          break;
        }
      }
    }

    // 4. Signal the visor's ready-gate that this canvas-video is ready.
    // eslint-disable-next-line no-console
    console.log(
      "[RecorderCanvasVideo] firing ready boxId=" +
        this.props.boxId +
        " hasOnReady=" +
        typeof this.props.onReady
    );
    this._fireReady();
  }

  _fireReady() {
    if (this._readyFiredForBoxId) return;
    this._readyFiredForBoxId = true;
    if (typeof this.props.onReady === "function") {
      try {
        this.props.onReady(this.props.boxId);
        // eslint-disable-next-line no-console
        console.log("[RecorderCanvasVideo] onReady invoked OK boxId=" + this.props.boxId);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log("[RecorderCanvasVideo] onReady threw: " + (e && e.message ? e.message : e));
      }
    } else {
      // eslint-disable-next-line no-console
      console.log("[RecorderCanvasVideo] no onReady prop! boxId=" + this.props.boxId);
    }
  }

  // Index of the last key chunk whose timestamp is at or before targetUs —
  // the valid decode entry point closest to a seek target. Falls back to 0
  // (streams start with a keyframe).
  _keyIndexAtOrBefore(targetUs) {
    let idx = 0;
    for (let i = 0; i < this._chunks.length; i++) {
      const c = this._chunks[i];
      if (c.timestamp > targetUs) break;
      if (c.type === "key") idx = i;
    }
    return idx;
  }

  // In-point/out-point of this clip inside the source file, in microseconds.
  // Split/trimmed pieces share one source URL — decoding must map scene time
  // into [trimStart, trimEnd], not the file's own [0, duration].
  _trimWindowUs() {
    const startUs = Math.max(0, Math.round((this.props.trimStartSec || 0) * 1e6));
    let endUs =
      typeof this.props.trimEndSec === "number" && this.props.trimEndSec > 0
        ? Math.round(this.props.trimEndSec * 1e6)
        : this._mediaDurationUs;
    if (this._mediaDurationUs > 0) endUs = Math.min(endUs, this._mediaDurationUs);
    return { startUs, endUs };
  }

  _seek(sceneTimeSec) {
    // Setup failed or produced no chunks — resolve immediately so the frame
    // loop isn't throttled by the 500 ms per-seek timeout below.
    if (!this._decoder || this._chunks.length === 0) return Promise.resolve();
    const localSec = Math.max(0, sceneTimeSec - (this.props.clipStartSec || 0));
    const { startUs: trimStartUs, endUs: trimEndUs } = this._trimWindowUs();
    let targetUs = trimStartUs + Math.round(localSec * 1e6);
    const windowUs = trimEndUs - trimStartUs;

    // Loop semantics mirror VisorCanvasSli/IdealTimeline's modulo: loop
    // restarts media within the clip's trim window.
    let wrapped = false;
    if (this.props.loop && windowUs > 0) {
      targetUs = trimStartUs + ((targetUs - trimStartUs) % windowUs);
      // Wrap detected when the new (already-modulo'd) target lands behind
      // where the previous seek left off — i.e. playback just cycled.
      // Guarded on _lastTargetUs >= 0 so the very first seek never rewinds.
      if (this._lastTargetUs >= 0 && targetUs < this._lastTargetUs) {
        wrapped = true;
        this._dropPendingFrames();
        // decoder.reset() leaves the VideoDecoder "unconfigured" per the
        // WebCodecs spec — configure() is mandatory before decode() works again.
        try {
          this._decoder.reset();
          this._decoder.configure(this._decoderConfig);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[RecorderCanvasVideo] loop rewind failed:", err.message);
        }
        this._chunkIndex = this._keyIndexAtOrBefore(targetUs);
      }
    } else if (windowUs > 0) {
      // Non-loop clips hold their out-point frame once the play window ends.
      targetUs = Math.min(targetUs, trimEndUs);
    }
    this._lastTargetUs = targetUs;

    // Rate fix: hold the painted frame while the target still falls inside
    // its display span. Without this every seek decoded and painted the NEXT
    // source frame — the canvas consumed exactly one source frame per output
    // frame, so a 24/25fps source rendered at a 30fps output played ~1.25x
    // fast while the FFmpeg audio mix stayed 1x (AV drift grew through the
    // scene). 30fps sources masked the bug (1:1 by coincidence). Skipped on
    // a loop wrap, which must re-decode from the earlier keyframe.
    const lp = this._lastPaintedFrame;
    if (!wrapped && lp && lp.duration && targetUs < lp.timestamp + lp.duration) {
      return Promise.resolve();
    }

    // Periodic seek log (every ~1s of scene time) to trace progress
    // without drowning the worker in messages.
    this._seekCount = (this._seekCount || 0) + 1;
    if (this._seekCount % 30 === 1 || this._seekCount <= 3) {
      // eslint-disable-next-line no-console
      console.log(
        "[RecorderCanvasVideo] seek boxId=" +
          this.props.boxId +
          " sceneT=" +
          sceneTimeSec.toFixed(2) +
          " localT=" +
          localSec.toFixed(2) +
          " chunkIdx=" +
          this._chunkIndex +
          "/" +
          this._chunks.length +
          " seekCount=" +
          this._seekCount
      );
    }

    // Resolve any prior in-flight seek with the last painted frame.
    if (this._currentSeekResolve) {
      const stale = this._currentSeekResolve;
      this._currentSeekResolve = null;
      stale();
    }

    // Frames decoded ahead during an earlier seek may already cover this one.
    while (this._pendingFrames.length > 0) {
      const frame = this._pendingFrames.shift();
      if (this._frameCoversTarget(frame, targetUs)) {
        this._paint(frame);
        this._currentSeekTargetUs = targetUs;
        return new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      }
      frame.close();
    }

    return new Promise((resolve) => {
      this._currentSeekResolve = resolve;
      this._currentSeekTargetUs = targetUs;
      this._feedNextChunk();

      // The decoder holds its first outputs until more input arrives
      // (reorder buffer), so one chunk per seek left a freshly mounted clip
      // unpainted — the host captured its black canvas for several frames at
      // every video change. Keep feeding while this seek waits on an idle queue.
      const nudge = setInterval(() => {
        if (this._currentSeekResolve !== resolve) {
          clearInterval(nudge);
          return;
        }
        if (this._decoder && this._decoder.decodeQueueSize === 0) this._feedNextChunk();
      }, 10);

      // Hard timeout — 500 ms. If decoder hasn't produced a frame at
      // target by then, canvas keeps last-painted frame and host
      // captures that. Better than deadlocking the loop.
      setTimeout(() => {
        clearInterval(nudge);
        if (this._currentSeekResolve === resolve) {
          this._currentSeekResolve = null;
          resolve();
        }
      }, 500);
    });
  }

  // A frame satisfies a seek when the target falls inside its display span
  // (timestamp..timestamp+duration) — not only at/after its start.
  _frameCoversTarget(frame, targetUs) {
    return (
      frame.timestamp >= targetUs ||
      (frame.duration > 0 && frame.timestamp + frame.duration > targetUs)
    );
  }

  _dropPendingFrames() {
    for (const frame of this._pendingFrames) {
      try {
        frame.close();
      } catch (_) {}
    }
    this._pendingFrames = [];
  }

  _feedNextChunk() {
    if (!this._decoder) return;
    if (this._chunkIndex >= this._chunks.length) return;
    if (this._decoder.decodeQueueSize > 4) return; // backpressure
    try {
      this._decoder.decode(this._chunks[this._chunkIndex]);
      this._chunkIndex++;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[RecorderCanvasVideo] decode threw:", err.message);
    }
  }

  _onDecodedFrame(frame) {
    if (!this._mounted) {
      frame.close();
      return;
    }
    // Comparing timestamp alone discarded the frame that actually covers the
    // target and painted its successor, skipping source frames.
    const coversTarget = this._frameCoversTarget(frame, this._currentSeekTargetUs);
    if (this._currentSeekResolve && coversTarget) {
      // This is the frame we've been waiting for. Paint and resolve.
      this._paint(frame);
      const r = this._currentSeekResolve;
      this._currentSeekResolve = null;
      // Two rAF ticks to ensure compositor includes our paint.
      requestAnimationFrame(() => requestAnimationFrame(r));
    } else if (this._currentSeekResolve && !coversTarget) {
      // Frame is older than what we want — discard, decode next.
      frame.close();
      this._feedNextChunk();
    } else {
      // No active seek — a frame decoded ahead. Keep it for the next seek;
      // before the first paint, show it so the canvas isn't blank.
      if (!this._lastPaintedFrame) this._paint(frame);
      else this._pendingFrames.push(frame);
    }
  }

  _paint(frame) {
    const c = this.canvasRef.current;
    if (!c) {
      frame.close();
      return;
    }
    if (c.width !== frame.codedWidth || c.height !== frame.codedHeight) {
      c.width = frame.codedWidth;
      c.height = frame.codedHeight;
    }
    const ctx = c.getContext("2d");
    if (ctx) {
      ctx.drawImage(frame, 0, 0, c.width, c.height);
    }
    if (this._lastPaintedFrame) {
      try {
        this._lastPaintedFrame.close();
      } catch (_) {}
    }
    this._lastPaintedFrame = frame;
  }

  render() {
    return (
      <canvas
        ref={this.canvasRef}
        style={{
          width: "100%",
          height: "100%",
          display: "block",
          backgroundColor: "#000",
        }}
      />
    );
  }
}
